<template>
  <v-col cols="12" class="ma-0 pa-0">
    <div class="time-series-statistics" :style="{ maxHeight: screen_height + 'px' }">
      <table class="statistics-table">
        <thead>
          <tr>
            <th class="metric-column">Metric</th>
            <th class="measure-column">Measure</th>
            <th
              v-for="(period, idx) in dataset"
              :key="idx"
              class="period-header"
            >
              {{ format_period(period) }}
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="metric in metric_groups">
            <tr
              v-for="(measure, measure_idx) in metric.measures"
              :key="metric.name + '-' + measure.key"
              :class="metric.row_class"
            >
              <td
                v-if="measure_idx === 0"
                class="metric-column metric-group-label"
                :class="metric.header_class"
                :rowspan="metric.measures.length"
              >
                {{ metric.name }}
              </td>
              <td class="measure-column">{{ measure.label }}</td>
              <td
                v-for="(period, period_idx) in dataset"
                :key="period_idx"
                :class="{ 'metric-value': measure.type === 'value' }"
              >
                <span v-if="measure.type === 'change'" :class="change_class(period[measure.key])">
                  {{ format_percentage(period[measure.key]) }}
                </span>
                <span v-else>
                  {{ format_spend(period[measure.key]) }}
                </span>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
  </v-col>
</template>

<script>
export default {
  name: "TimeSeriesStatistics",
  props: {
    dataset: {
      type: Array,
      default: () => [],
    },
    screen_height: {
      type: Number,
      required: true,
    },
  },
  computed: {
    metric_groups: function () {
      return [
        {
          name: "Spend",
          header_class: "spend-group",
          row_class: "spend-row",
          measures: [
            { label: "Value", key: "spend", type: "value" },
            { label: "QoQ", key: "qoq_spend", type: "change" },
            { label: "YoY", key: "yoy_spend", type: "change" },
          ],
        },
        {
          name: "ROAS",
          header_class: "roas-group",
          row_class: "roas-row",
          measures: [
            { label: "Value", key: "roas", type: "value" },
            { label: "QoQ", key: "qoq_roas", type: "change" },
            { label: "YoY", key: "yoy_roas", type: "change" },
          ],
        },
        {
          name: "Volume",
          header_class: "volume-group",
          row_class: "volume-row",
          measures: [
            { label: "Value", key: "sales_attribution", type: "value" },
            { label: "QoQ", key: "qoq_sales_attribution", type: "change" },
            { label: "YoY", key: "yoy_sales_attribution", type: "change" },
          ],
        },
      ];
    },
  },
  methods: {
    format_period: function (period) {
      if (!period) {
        return "-";
      }
      return period["y"] + " - Q" + period["q"];
    },
    format_percentage: function (stat) {
      if (stat == 0 || stat === "-" || stat == null) {
        return "-";
      }
      return (stat * 100).toLocaleString("en-CA", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
        notation: "compact",
      }) + "%";
    },
    format_spend: function (stat) {
      if (stat == 0 || stat === "-" || stat == null) {
        return "-";
      }
      return stat.toLocaleString("en-CA", {
        style: "currency",
        currency: "CAD",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
        notation: "compact",
      });
    },
    change_class: function (stat) {
      if (stat === "-" || stat == null || stat == 0) {
        return "change-pill neutral-change";
      }
      return stat < 0 ? "change-pill negative-change" : "change-pill positive-change";
    },
  },
};
</script>

<style scoped>
.time-series-statistics {
  border: 1px solid #e6e8ec;
  overflow: auto;
  width: 100%;
}

.statistics-table {
  border-collapse: collapse;
  color: #1f2933;
  font-family: "Poppins";
  font-size: 0.82em;
  min-width: 860px;
  table-layout: fixed;
  width: 100%;
}

.statistics-table th {
  border-bottom: 1px solid #dfe3e8;
  color: #52616f;
  font-weight: 600;
  padding: 12px 16px;
  position: sticky;
  text-align: center;
  top: 0;
  z-index: 3;
}

.statistics-table td {
  border-bottom: 1px solid #edf0f2;
  font-weight: 400;
  padding: 12px 16px;
  text-align: center;
  white-space: nowrap;
}

.statistics-table tbody tr:last-child td {
  border-bottom: 0;
}

.metric-column,
.measure-column {
  background: #ffffff;
  left: 0;
  text-align: left;
}

.metric-column {
  width: 126px;
}

.measure-column {
  left: 126px;
  text-align: left !important;
  width: 108px;
}

.statistics-table th.metric-column,
.statistics-table th.measure-column {
  background: #f8fafc;
  text-align: left;
  z-index: 4;
}

.statistics-table td.metric-column,
.statistics-table td.measure-column {
  position: sticky;
  z-index: 2;
}

.period-header {
  background: #f8fafc;
  min-width: 132px;
}

.metric-group {
  border-left: 1px solid #dfe3e8;
  color: #26323f;
  font-size: 0.86em;
  letter-spacing: 0;
  text-align: center !important;
}

.spend-group {
  background: #eaf4ff;
}

.roas-group {
  background: #eaf7ee;
}

.volume-group {
  background: #fff2e4;
}

.value-header,
.change-header {
  color: #6b7785;
  font-size: 0.78em;
  text-transform: uppercase;
}

.spend-row td {
  background: #f5faff;
}

.roas-row td {
  background: #f5fbf7;
}

.volume-row td {
  background: #fff8f0;
}

.statistics-table tbody tr.spend-row:hover td {
  background: #edf6ff;
}

.statistics-table tbody tr.roas-row:hover td {
  background: #edf8f1;
}

.statistics-table tbody tr.volume-row:hover td {
  background: #fff0e0;
}

.metric-group-label {
  border-right: 1px solid #dfe3e8;
  color: #26323f;
  font-weight: 700 !important;
  text-align: left !important;
  vertical-align: middle;
}

.metric-value {
  color: #17212b;
  font-weight: 600 !important;
}

.group-start {
  border-left: 1px solid #dfe3e8;
}

.group-end {
  border-right: 1px solid #edf0f2;
}

.change-pill {
  border-radius: 999px;
  display: inline-block;
  font-size: 0.92em;
  font-weight: 600 !important;
  min-width: 60px;
  padding: 5px 10px;
  text-align: center;
}

.positive-change {
  background: #eaf7ee;
  color: #14853d;
}

.negative-change {
  background: #fdeeee;
  color: #c93f3f;
}

.neutral-change {
  background: #f1f3f5;
  color: #7b8794;
}
</style>
